import React, { FunctionComponent } from 'react';
import { useFormik } from 'formik';
import { trim } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { nameValidationSchema } from '../../../../common/validation/yup';
import RegularButton from '../../../../components/RegularButton';
import { IReferenceCandidateForm } from '../../../../common/models/reference/IReferenceCandidateForm';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import Input from '../../../../components/Input';
import styles from './styles.module.scss';
import { useSwipeable } from 'react-swipeable';

type ReferenceName = {
  id?: string;
  firstName: string;
  lastName: string;
}

interface IReferenceNameFormProps {
  value: ReferenceName;
  references: IReferenceCandidateForm[];
  onGoBack: IBindingAction;
  onSubmit: IBindingCallback1<ReferenceName>;
}

const ReferenceNameForm: FunctionComponent<IReferenceNameFormProps> = ({ value, references, onGoBack, onSubmit }) => {
  const initialValues: ReferenceName = {
    id: value?.id || (references.length + 1).toString(),
    firstName: value.firstName || '',
    lastName: value.lastName || ''
  };

  const submit = ({ id, firstName, lastName }: ReferenceName) => onSubmit({
    id,
    firstName: trim(firstName),
    lastName: trim(lastName)
  });

  const { handleSubmit, setFieldValue, values, isValid } = useFormik({
    initialValues,
    onSubmit: submit,
    validationSchema: nameValidationSchema,
    validateOnMount: true
  });

  const swipeHandler = useSwipeable({
    onSwipedLeft: () => handleSubmit(),
    onSwipedRight: onGoBack,
    trackMouse: false
  });

  return (
    <div {...swipeHandler} className="cf-container">
      <div className="cf-header-title mb-2">
        <FormattedMessage id="reference" defaultMessage="Reference" />
        &nbsp;
        {initialValues.id}
      </div>
      <div className="cf-title">
        <FormattedMessage id="candidateForm.name.title" defaultMessage="What is your reference’s name?" />
      </div>
      <form onSubmit={handleSubmit} className="w-100" autoComplete="off">
        <div className={`${styles.inputsContainer} cf-inputs-container`}>
          <Input
            autoFocus
            type="text"
            value={values.firstName}
            label={{ id: 'firstName', defaultMessage: 'First name' }}
            onChange={v => setFieldValue('firstName', v)}
            size="lg"
            variant="underlined"
          />
          <Input
            type="text"
            label={{ id: 'lastName', defaultMessage: 'Last name' }}
            value={values.lastName}
            onChange={v => setFieldValue('lastName', v)}
            size="lg"
            variant="underlined"
          />
        </div>
        <div className="cf-footer-btns">
          <IconButton
            size="lg"
            icon={<Icon icon="arrowLeft" />}
            onClick={onGoBack}
          />
          <RegularButton
            disabled={!isValid}
            size="lg"
            textIntlId="candidateForm.next"
            defaultMessage="Next"
            icon={<Icon icon="arrowRight" />}
            iconPosition="right"
            onClick={handleSubmit}
            type="submit"
          />
        </div>
      </form>
    </div>
  );
};

export default ReferenceNameForm;
